import React, { useState } from 'react';
import { ArrowLeft, Mail, MessageSquare, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

const LEGAL_CONTENT = {
  privacy: {
    en: {
      title: "Privacy Policy",
      lastUpdated: "Last updated: June 2026",
      content: `
PlateMind Nutrition ("PlateMind", "we", "us") provides dietitian-led nutrition care, including consultations, a 12-week programme, meal plans and clinical reports. This policy explains what personal data we collect, why we use it and the choices you have. We process personal data in line with the Nigeria Data Protection Act 2023 (NDPA) and related guidance from the Nigeria Data Protection Commission.

## Who is responsible for your data

PlateMind Nutrition is the data controller for the personal data described in this policy. Where you are referred to us by a clinic, employer or other partner organisation, that organisation may also be a controller for the information it shares with us.

## Information we collect

- Identity and contact details: name, email address, phone or WhatsApp number, location and preferred contact method
- Health and clinical information: medical history, diagnosed conditions, medications, allergies, measurements (such as weight, height, blood pressure and blood glucose), laboratory results, symptoms, lifestyle and dietary habits, and food preferences
- Information you give us in forms: pre-consultation intake forms, food preference forms, weekly check-ins and the Week 12 programme review
- Consultation records: appointment details, clinical notes written by your dietitian, meal plans and clinical reports
- Payment information: programme selection, amounts paid and payment references. Card details are entered directly with our payment provider, Paystack. We do not see or store your full card number
- Communication records: emails and WhatsApp messages we send you, reminders, and whether they were delivered, opened or acted on (for example, confirming attendance or rescheduling)
- Technical information: basic device, browser and usage data needed to keep the service secure and working

Health information is sensitive personal data. We only collect what your dietitian needs to provide safe and effective care.

## How we use your information

- To deliver your consultations and the 12-week programme, including scheduling, video meeting links, reminders and follow-ups
- To prepare personalised meal plans, clinical reports and progress summaries
- To send weekly check-ins, review your answers and flag anything your dietitian should follow up on
- To process payments, renewals and payment reminders
- To contact you about your care, appointments and account
- To share reports with your doctor or another healthcare professional when you ask us to or agree to it
- To keep our services secure, prevent misuse and meet our legal and professional obligations
- With your consent, to send newsletters or educational content. You can unsubscribe at any time

## Legal basis for processing

We process your data because it is necessary to provide the services you have requested (contract), because you have given consent (in particular for health information and marketing), because we must comply with legal obligations, or because we have a legitimate interest in running a safe and effective service. Where we rely on consent, you can withdraw it at any time. This does not affect processing that took place before you withdrew.

## Use of AI tools

We use artificial intelligence tools to help our dietitians draft meal plans, clinical summaries and reports. AI output is always reviewed, edited and approved by a qualified dietitian before it is shared with you. AI is not used to make automated decisions about your care without human review. Information sent to AI providers is limited to what is needed for the task.

## Who we share your information with

We do not sell your personal data. We share it only with:
- Your PlateMind dietitian and authorised PlateMind staff involved in your care
- A referring clinic or partner organisation, where you joined through them, limited to what is needed for your care
- Your doctor or other healthcare professionals, when you ask us to or agree to it
- Service providers who help us run PlateMind, including Paystack (payments), Zoho (email and booking), Google (calendar and video meetings), WhatsApp (messaging), our cloud hosting provider and AI service providers (such as OpenAI and Anthropic)
- Regulators, law enforcement or courts, where we are required by law

Our service providers may only use your data to provide services to us and must protect it.

## International transfers

Some of our service providers store or process data outside Nigeria. When this happens, we take steps required by the NDPA to make sure your data is adequately protected, such as using providers with appropriate safeguards and contractual protections.

## How long we keep your information

We keep clinical records, consultation notes, meal plans and reports for as long as needed to provide your care and to meet professional and legal record-keeping requirements. Payment records are kept as required for accounting and tax purposes. When data is no longer needed, we delete or anonymise it securely.

## How we protect your information

We use appropriate technical and organisational measures to protect your data, including encrypted connections (HTTPS), access controls so that only authorised staff can view patient records, secure personal links for forms and appointments, and trusted hosting providers. No system is completely secure. If a data breach is likely to put your rights at risk, we will notify you and the Nigeria Data Protection Commission as required by law.

## Personal links in emails and messages

Check-in, booking and appointment emails contain personal links so you can complete forms, confirm attendance or reschedule without signing in. Please do not forward these links to others. If you think a link has been shared by mistake, contact us and we will issue a new one.

## Your rights

Under the NDPA you have the right to:
- Be told how your data is used
- Access a copy of your personal data
- Ask us to correct inaccurate or incomplete data
- Ask us to delete your data, where we are not required to keep it
- Restrict or object to certain processing
- Receive your data in a portable format
- Withdraw consent at any time
- Opt out of marketing messages
- Complain to the Nigeria Data Protection Commission (ndpc.gov.ng)

To exercise any of these rights, email privacy@platemind.app. We may need to confirm your identity before responding.

## Children

Our services are intended for adults. If a patient under 18 is cared for by PlateMind, a parent or guardian must provide consent and manage communications on their behalf.

## Changes to this policy

We may update this policy from time to time. The date at the top shows when it was last updated. If we make significant changes, we will let you know by email or through the service.

## Contact us

PlateMind Nutrition, Lagos, Nigeria
Privacy questions: privacy@platemind.app
General enquiries: hello@platemind.app
Phone / WhatsApp: +234 706 203 9281
      `
    },
    es: {
      title: "Política de Privacidad",
      lastUpdated: "Última actualización: Marzo 2026",
      content: `
## Información que Recopilamos

PlateMind recopila información que usted proporciona directamente, incluyendo:
- Información de cuenta (nombre, correo electrónico, contraseña)
- Datos de salud (registros de comidas, lecturas de glucosa, preferencias dietéticas)
- Datos de uso (interacciones con la app, uso de funciones)

## Cómo Usamos Su Información

Usamos su información para:
- Proporcionar recomendaciones nutricionales personalizadas
- Rastrear su progreso de salud
- Mejorar nuestros algoritmos de IA
- Enviar notificaciones importantes

## Seguridad de Datos

Implementamos medidas de seguridad estándar de la industria para proteger sus datos de salud.

## Sus Derechos

Usted tiene derecho a:
- Acceder a sus datos
- Solicitar la eliminación de datos
- Exportar sus registros de salud
- Optar por no recibir comunicaciones no esenciales

## Contáctenos

Para inquietudes sobre privacidad, contacte: privacy@platemind.app
      `
    },
    fr: {
      title: "Politique de Confidentialité",
      lastUpdated: "Dernière mise à jour: Mars 2026",
      content: `
## Informations que Nous Collectons

PlateMind collecte les informations que vous fournissez directement, notamment:
- Informations de compte (nom, email, mot de passe)
- Données de santé (journaux de repas, lectures de glycémie, préférences alimentaires)
- Données d'utilisation (interactions avec l'app, utilisation des fonctionnalités)

## Comment Nous Utilisons Vos Informations

Nous utilisons vos informations pour:
- Fournir des recommandations nutritionnelles personnalisées
- Suivre vos progrès de santé
- Améliorer nos algorithmes d'IA
- Envoyer des notifications importantes

## Sécurité des Données

Nous mettons en œuvre des mesures de sécurité standard de l'industrie pour protéger vos données de santé.

## Vos Droits

Vous avez le droit de:
- Accéder à vos données
- Demander la suppression des données
- Exporter vos dossiers de santé
- Refuser les communications non essentielles

## Nous Contacter

Pour les questions de confidentialité, contactez: privacy@platemind.app
      `
    },
    pt: {
      title: "Política de Privacidade",
      lastUpdated: "Última atualização: Março 2026",
      content: `
## Informações que Coletamos

A PlateMind coleta informações que você fornece diretamente, incluindo:
- Informações da conta (nome, e-mail, senha)
- Dados de saúde (registros de refeições, leituras de glicose, preferências alimentares)
- Dados de uso (interações com o app, uso de recursos)

## Como Usamos Suas Informações

Usamos suas informações para:
- Fornecer recomendações nutricionais personalizadas
- Acompanhar seu progresso de saúde
- Melhorar nossos algoritmos de IA
- Enviar notificações importantes

## Segurança de Dados

Implementamos medidas de segurança padrão da indústria para proteger seus dados de saúde.

## Seus Direitos

Você tem o direito de:
- Acessar seus dados
- Solicitar exclusão de dados
- Exportar seus registros de saúde
- Recusar comunicações não essenciais

## Contato

Para questões de privacidade, entre em contato: privacy@platemind.app
      `
    },
    ar: {
      title: "سياسة الخصوصية",
      lastUpdated: "آخر تحديث: مارس 2026",
      content: `
## المعلومات التي نجمعها

تجمع PlateMind المعلومات التي تقدمها مباشرة، بما في ذلك:
- معلومات الحساب (الاسم، البريد الإلكتروني، كلمة المرور)
- البيانات الصحية (سجلات الوجبات، قراءات الجلوكوز، التفضيلات الغذائية)
- بيانات الاستخدام (التفاعلات مع التطبيق، استخدام الميزات)

## كيف نستخدم معلوماتك

نستخدم معلوماتك لـ:
- تقديم توصيات غذائية مخصصة
- تتبع تقدمك الصحي
- تحسين خوارزميات الذكاء الاصطناعي
- إرسال إشعارات مهمة

## أمان البيانات

نطبق إجراءات أمان قياسية في الصناعة لحماية بياناتك الصحية.

## حقوقك

لديك الحق في:
- الوصول إلى بياناتك
- طلب حذف البيانات
- تصدير سجلاتك الصحية
- إلغاء الاشتراك في الاتصالات غير الضرورية

## اتصل بنا

لمخاوف الخصوصية، اتصل: privacy@platemind.app
      `
    },
    hi: {
      title: "गोपनीयता नीति",
      lastUpdated: "अंतिम अपडेट: मार्च 2026",
      content: `
## हम जो जानकारी एकत्र करते हैं

PlateMind आपके द्वारा सीधे प्रदान की गई जानकारी एकत्र करता है, जिसमें शामिल हैं:
- खाता जानकारी (नाम, ईमेल, पासवर्ड)
- स्वास्थ्य डेटा (भोजन लॉग, ग्लूकोज रीडिंग, आहार प्राथमिकताएं)
- उपयोग डेटा (ऐप इंटरैक्शन, फीचर उपयोग)

## हम आपकी जानकारी का उपयोग कैसे करते हैं

हम आपकी जानकारी का उपयोग करते हैं:
- व्यक्तिगत पोषण सिफारिशें प्रदान करने के लिए
- आपकी स्वास्थ्य प्रगति को ट्रैक करने के लिए
- हमारे AI एल्गोरिदम को बेहतर बनाने के लिए
- महत्वपूर्ण सूचनाएं भेजने के लिए

## डेटा सुरक्षा

हम आपके स्वास्थ्य डेटा की सुरक्षा के लिए उद्योग-मानक सुरक्षा उपाय लागू करते हैं।

## आपके अधिकार

आपको अधिकार है:
- अपने डेटा तक पहुंचने का
- डेटा हटाने का अनुरोध करने का
- अपने स्वास्थ्य रिकॉर्ड निर्यात करने का
- गैर-आवश्यक संचार से बाहर निकलने का

## संपर्क करें

गोपनीयता संबंधी चिंताओं के लिए संपर्क करें: privacy@platemind.app
      `
    },
    zh: {
      title: "隐私政策",
      lastUpdated: "最后更新：2026年3月",
      content: `
## 我们收集的信息

PlateMind 收集您直接提供的信息，包括：
- 账户信息（姓名、电子邮件、密码）
- 健康数据（饮食记录、血糖读数、饮食偏好）
- 使用数据（应用交互、功能使用）

## 我们如何使用您的信息

我们使用您的信息来：
- 提供个性化营养建议
- 跟踪您的健康进展
- 改进我们的AI算法
- 发送重要通知

## 数据安全

我们实施行业标准的安全措施来保护您的健康数据。

## 您的权利

您有权：
- 访问您的数据
- 请求删除数据
- 导出您的健康记录
- 选择退出非必要通信

## 联系我们

如有隐私问题，请联系：privacy@platemind.app
      `
    }
  },
  terms: {
    en: {
      title: "Terms of Service",
      lastUpdated: "Last updated: June 2026",
      content: `
These Terms of Service ("Terms") govern your use of the services provided by PlateMind Nutrition ("PlateMind", "we", "us"), including our website, booking pages, forms, consultations and the 12-week nutrition programme. By registering, paying for a programme, booking a consultation or using our forms, you agree to these Terms and our Privacy Policy.

## Our services

PlateMind provides dietitian-led nutrition care. Depending on the programme you choose, our services may include:
- An initial consultation with a qualified dietitian, held by video call
- Pre-consultation intake and food preference forms
- Personalised meal plans and nutrition guidance
- Clinical reports and progress summaries, which can be shared with your doctor at your request
- Weekly check-ins during the programme and optional follow-up sessions
- A Week 12 programme review with options to complete, continue or renew

Meal plans and reports may be drafted with the help of AI tools. They are always reviewed and approved by a qualified dietitian before they are shared with you.

## Medical disclaimer

**PlateMind provides nutrition care and education. It is not an emergency service and does not replace your doctor.**
- Our dietitians do not diagnose medical conditions or prescribe or change medication
- Continue to follow the advice of your doctor and do not stop or change medication without speaking to them
- If you have urgent symptoms (for example, very high or very low blood glucose, chest pain, fainting or difficulty breathing), contact your doctor or go to the nearest hospital immediately
- Results vary from person to person and we cannot guarantee specific health outcomes

## Eligibility and accurate information

You must be 18 or older to use PlateMind, or have a parent or guardian register and consent on your behalf. You agree to give complete and accurate information about your health, medications and circumstances, and to tell us about any changes. Your dietitian relies on this information to give safe advice.

## Registration and payment

- Programme prices are shown on the registration page in Nigerian Naira (NGN) before you pay
- Payments are processed securely by Paystack. Your programme starts once payment is confirmed
- If you register but do not complete payment, we may send you reminders. Your place is not reserved until payment is received
- Renewals at the end of the 12-week programme are optional and are only charged when you choose to renew and complete payment. There is no automatic renewal
- Prices may change, but changes will not affect a programme you have already paid for

## Booking, rescheduling and attendance

- Consultations are booked in West Africa Time (WAT) and last about 45 minutes, unless stated otherwise
- Appointments must be booked at least 24 hours in advance
- We send a reminder about 48 hours before your appointment, with options to confirm your attendance or reschedule
- You can reschedule online up to 24 hours before your appointment, up to two times per booking. Within 24 hours, please contact us on WhatsApp and we will help where we can
- Please join on time so you get the full session
- Missed sessions without notice are forfeited and will not be refunded or automatically rebooked
- If we need to cancel or move an appointment, we will contact you and offer a new time at no extra cost

## Refunds and cancellations

- No refunds are given once the programme has started. The programme starts when your initial consultation takes place or your first programme materials are delivered, whichever comes first
- If you cancel before the programme starts, contact hello@platemind.app and we will review your request
- If we are unable to deliver a service you have paid for, we will offer a replacement session or a refund for that service
- Nothing in these Terms affects your rights under Nigerian consumer protection law

## Personal links and your account

Emails and messages from PlateMind contain personal links for your forms, check-ins and appointments. Keep these links private and do not share them. If you have a PlateMind account, keep your login details confidential and tell us straight away if you think someone else has access to it.

## Acceptable use

You agree not to:
- Use PlateMind for anyone other than yourself (or the person you are registered to care for)
- Share, sell or publish your meal plans, reports or programme materials for commercial purposes
- Misuse our forms, booking system or communications, or try to access other people's information
- Behave abusively towards our staff

We may suspend or end your access if you break these Terms.

## Intellectual property

Our meal plans, reports, programme materials, branding and website content belong to PlateMind Nutrition. You may use materials provided to you for your own personal care and share them with your doctor or family members who support you.

## Third-party services

We use trusted third parties to deliver our services, including Paystack (payments), Google Meet (video consultations), Zoho (email and bookings) and WhatsApp (messaging). Your use of these services may also be subject to their own terms.

## Limitation of liability

To the extent permitted by law:
- PlateMind is not responsible for health outcomes resulting from information you did not disclose, advice you chose not to follow, or decisions made without consulting your doctor
- We are not liable for interruptions caused by internet, device or third-party service problems outside our control
- Our total liability for any claim relating to our services is limited to the amount you paid for the programme or service concerned

Nothing in these Terms limits liability that cannot be limited by law, including liability for negligence causing death or personal injury.

## Changes to these Terms

We may update these Terms from time to time. The date at the top shows when they were last updated. Changes do not apply to a programme you have already paid for unless the change is required by law.

## Governing law

These Terms are governed by the laws of the Federal Republic of Nigeria. Any dispute will first be handled through good-faith discussion. If it cannot be resolved, it will be subject to the courts of Lagos State, Nigeria.

## Contact

PlateMind Nutrition, Lagos, Nigeria
Email: hello@platemind.app
Privacy: privacy@platemind.app
Phone / WhatsApp: +234 706 203 9281
      `
    },
    es: {
      title: "Términos de Servicio",
      lastUpdated: "Última actualización: Marzo 2026",
      content: `
## Aceptación de Términos

Al usar PlateMind, usted acepta estos Términos de Servicio y nuestra Política de Privacidad.

## Descripción del Servicio

PlateMind proporciona orientación nutricional impulsada por IA para el manejo de la diabetes. Nuestro servicio incluye:
- Análisis de comidas y estimación de carbohidratos
- Predicciones de impacto glucémico
- Recomendaciones personalizadas

## Descargo de Responsabilidad Médica

**PlateMind no es un dispositivo médico.** La información proporcionada:
- No reemplaza el consejo médico profesional
- No debe usarse para diagnóstico o tratamiento
- Es solo para fines educativos e informativos

Siempre consulte a un profesional de la salud calificado antes de hacer cambios dietéticos.

## Responsabilidades del Usuario

Usted acepta:
- Proporcionar información de salud precisa
- Usar el servicio para propósitos personales, no comerciales
- No compartir sus credenciales de cuenta
- Reportar cualquier inexactitud en nuestras recomendaciones

## Contacto

questions@platemind.app
      `
    },
    fr: {
      title: "Conditions d'Utilisation",
      lastUpdated: "Dernière mise à jour: Mars 2026",
      content: `
## Acceptation des Conditions

En utilisant PlateMind, vous acceptez ces Conditions d'Utilisation et notre Politique de Confidentialité.

## Description du Service

PlateMind fournit des conseils nutritionnels alimentés par l'IA pour la gestion du diabète. Notre service comprend:
- Analyse des repas et estimation des glucides
- Prédictions d'impact glycémique
- Recommandations personnalisées

## Avertissement Médical

**PlateMind n'est pas un dispositif médical.** Les informations fournies:
- Ne remplacent pas les conseils médicaux professionnels
- Ne doivent pas être utilisées pour le diagnostic ou le traitement
- Sont à des fins éducatives et informatives uniquement

Consultez toujours un professionnel de santé qualifié avant de modifier votre alimentation.

## Responsabilités de l'Utilisateur

Vous acceptez de:
- Fournir des informations de santé exactes
- Utiliser le service à des fins personnelles, non commerciales
- Ne pas partager vos identifiants de compte
- Signaler toute inexactitude dans nos recommandations

## Contact

questions@platemind.app
      `
    },
    pt: {
      title: "Termos de Serviço",
      lastUpdated: "Última atualização: Março 2026",
      content: `
## Aceitação dos Termos

Ao usar a PlateMind, você concorda com estes Termos de Serviço e nossa Política de Privacidade.

## Descrição do Serviço

A PlateMind fornece orientação nutricional baseada em IA para o gerenciamento do diabetes. Nosso serviço inclui:
- Análise de refeições e estimativa de carboidratos
- Previsões de impacto glicêmico
- Recomendações personalizadas

## Aviso Médico

**A PlateMind não é um dispositivo médico.** As informações fornecidas:
- Não substituem o conselho médico profissional
- Não devem ser usadas para diagnóstico ou tratamento
- São apenas para fins educacionais e informativos

Sempre consulte um profissional de saúde qualificado antes de fazer alterações na dieta.

## Responsabilidades do Usuário

Você concorda em:
- Fornecer informações de saúde precisas
- Usar o serviço para fins pessoais, não comerciais
- Não compartilhar suas credenciais de conta
- Relatar quaisquer imprecisões em nossas recomendações

## Contato

questions@platemind.app
      `
    },
    ar: {
      title: "شروط الخدمة",
      lastUpdated: "آخر تحديث: مارس 2026",
      content: `
## قبول الشروط

باستخدام PlateMind، فإنك توافق على شروط الخدمة هذه وسياسة الخصوصية الخاصة بنا.

## وصف الخدمة

تقدم PlateMind إرشادات غذائية مدعومة بالذكاء الاصطناعي لإدارة مرض السكري. تشمل خدمتنا:
- تحليل الوجبات وتقدير الكربوهيدرات
- توقعات التأثير على سكر الدم
- توصيات مخصصة

## إخلاء المسؤولية الطبية

**PlateMind ليست جهازاً طبياً.** المعلومات المقدمة:
- لا تحل محل المشورة الطبية المهنية
- لا ينبغي استخدامها للتشخيص أو العلاج
- هي لأغراض تعليمية وإعلامية فقط

استشر دائماً أخصائي رعاية صحية مؤهل قبل إجراء تغييرات غذائية.

## مسؤوليات المستخدم

أنت توافق على:
- تقديم معلومات صحية دقيقة
- استخدام الخدمة لأغراض شخصية غير تجارية
- عدم مشاركة بيانات اعتماد حسابك
- الإبلاغ عن أي عدم دقة في توصياتنا

## الاتصال

questions@platemind.app
      `
    },
    hi: {
      title: "सेवा की शर्तें",
      lastUpdated: "अंतिम अपडेट: मार्च 2026",
      content: `
## शर्तों की स्वीकृति

PlateMind का उपयोग करके, आप इन सेवा की शर्तों और हमारी गोपनीयता नीति से सहमत होते हैं।

## सेवा विवरण

PlateMind मधुमेह प्रबंधन के लिए AI-संचालित पोषण मार्गदर्शन प्रदान करता है। हमारी सेवा में शामिल हैं:
- भोजन विश्लेषण और कार्बोहाइड्रेट अनुमान
- ग्लाइसेमिक प्रभाव पूर्वानुमान
- व्यक्तिगत सिफारिशें

## चिकित्सा अस्वीकरण

**PlateMind एक चिकित्सा उपकरण नहीं है।** प्रदान की गई जानकारी:
- पेशेवर चिकित्सा सलाह का विकल्प नहीं है
- निदान या उपचार के लिए उपयोग नहीं की जानी चाहिए
- केवल शैक्षिक और सूचनात्मक उद्देश्यों के लिए है

आहार में बदलाव करने से पहले हमेशा योग्य स्वास्थ्य पेशेवर से परामर्श करें।

## उपयोगकर्ता जिम्मेदारियां

आप सहमत हैं:
- सटीक स्वास्थ्य जानकारी प्रदान करने के लिए
- व्यक्तिगत, गैर-वाणिज्यिक उद्देश्यों के लिए सेवा का उपयोग करने के लिए
- अपने खाते के क्रेडेंशियल साझा न करने के लिए
- हमारी सिफारिशों में किसी भी अशुद्धि की रिपोर्ट करने के लिए

## संपर्क

questions@platemind.app
      `
    },
    zh: {
      title: "服务条款",
      lastUpdated: "最后更新：2026年3月",
      content: `
## 条款接受

使用 PlateMind，即表示您同意本服务条款和我们的隐私政策。

## 服务描述

PlateMind 提供 AI 驱动的营养指导，用于糖尿病管理。我们的服务包括：
- 餐食分析和碳水化合物估算
- 血糖影响预测
- 个性化建议

## 医疗免责声明

**PlateMind 不是医疗设备。** 提供的信息：
- 不能替代专业医疗建议
- 不应用于诊断或治疗
- 仅用于教育和信息目的

在进行饮食改变之前，请务必咨询合格的医疗专业人员。

## 用户责任

您同意：
- 提供准确的健康信息
- 将服务用于个人、非商业目的
- 不分享您的账户凭据
- 报告我们建议中的任何不准确之处

## 联系方式

questions@platemind.app
      `
    }
  }
};

const CONTACT_TRANSLATIONS = {
  en: {
    title: "Contact Us",
    subtitle: "We'd love to hear from you",
    back: "Back",
    generalInquiries: "General Inquiries",
    support: "Support",
    partnerships: "Partnerships",
    name: "Name",
    email: "Email",
    subject: "Subject",
    message: "Message",
    yourName: "Your name",
    yourEmail: "your@email.com",
    howCanWeHelp: "How can we help?",
    sendMessage: "Send Message",
    messageSent: "Message Sent!",
    thankYou: "Thank you for reaching out. We'll get back to you within 24-48 hours.",
    subjectOptions: {
      general: "General Inquiry",
      support: "Technical Support",
      partnership: "Partnership Opportunity",
      feedback: "Feedback",
      bug: "Report a Bug"
    }
  },
  es: {
    title: "Contáctenos",
    subtitle: "Nos encantaría saber de usted",
    back: "Volver",
    generalInquiries: "Consultas Generales",
    support: "Soporte",
    partnerships: "Asociaciones",
    name: "Nombre",
    email: "Correo Electrónico",
    subject: "Asunto",
    message: "Mensaje",
    yourName: "Tu nombre",
    yourEmail: "tu@email.com",
    howCanWeHelp: "¿Cómo podemos ayudarte?",
    sendMessage: "Enviar Mensaje",
    messageSent: "¡Mensaje Enviado!",
    thankYou: "Gracias por contactarnos. Te responderemos dentro de 24-48 horas.",
    subjectOptions: {
      general: "Consulta General",
      support: "Soporte Técnico",
      partnership: "Oportunidad de Asociación",
      feedback: "Comentarios",
      bug: "Reportar un Error"
    }
  },
  fr: {
    title: "Contactez-nous",
    subtitle: "Nous aimerions vous entendre",
    back: "Retour",
    generalInquiries: "Demandes Générales",
    support: "Support",
    partnerships: "Partenariats",
    name: "Nom",
    email: "Email",
    subject: "Sujet",
    message: "Message",
    yourName: "Votre nom",
    yourEmail: "votre@email.com",
    howCanWeHelp: "Comment pouvons-nous vous aider?",
    sendMessage: "Envoyer le Message",
    messageSent: "Message Envoyé!",
    thankYou: "Merci de nous avoir contacté. Nous vous répondrons dans les 24-48 heures.",
    subjectOptions: {
      general: "Demande Générale",
      support: "Support Technique",
      partnership: "Opportunité de Partenariat",
      feedback: "Commentaires",
      bug: "Signaler un Bug"
    }
  },
  pt: {
    title: "Contato",
    subtitle: "Adoraríamos ouvir de você",
    back: "Voltar",
    generalInquiries: "Consultas Gerais",
    support: "Suporte",
    partnerships: "Parcerias",
    name: "Nome",
    email: "E-mail",
    subject: "Assunto",
    message: "Mensagem",
    yourName: "Seu nome",
    yourEmail: "seu@email.com",
    howCanWeHelp: "Como podemos ajudar?",
    sendMessage: "Enviar Mensagem",
    messageSent: "Mensagem Enviada!",
    thankYou: "Obrigado por entrar em contato. Responderemos em 24-48 horas.",
    subjectOptions: {
      general: "Consulta Geral",
      support: "Suporte Técnico",
      partnership: "Oportunidade de Parceria",
      feedback: "Feedback",
      bug: "Reportar um Bug"
    }
  },
  ar: {
    title: "اتصل بنا",
    subtitle: "نحن نحب أن نسمع منك",
    back: "رجوع",
    generalInquiries: "استفسارات عامة",
    support: "الدعم",
    partnerships: "الشراكات",
    name: "الاسم",
    email: "البريد الإلكتروني",
    subject: "الموضوع",
    message: "الرسالة",
    yourName: "اسمك",
    yourEmail: "بريدك@email.com",
    howCanWeHelp: "كيف يمكننا مساعدتك؟",
    sendMessage: "إرسال الرسالة",
    messageSent: "تم إرسال الرسالة!",
    thankYou: "شكراً للتواصل معنا. سنرد عليك خلال 24-48 ساعة.",
    subjectOptions: {
      general: "استفسار عام",
      support: "الدعم الفني",
      partnership: "فرصة شراكة",
      feedback: "ملاحظات",
      bug: "الإبلاغ عن خطأ"
    }
  },
  hi: {
    title: "संपर्क करें",
    subtitle: "हम आपसे सुनना पसंद करेंगे",
    back: "वापस",
    generalInquiries: "सामान्य पूछताछ",
    support: "सहायता",
    partnerships: "साझेदारी",
    name: "नाम",
    email: "ईमेल",
    subject: "विषय",
    message: "संदेश",
    yourName: "आपका नाम",
    yourEmail: "आपका@email.com",
    howCanWeHelp: "हम आपकी कैसे मदद कर सकते हैं?",
    sendMessage: "संदेश भेजें",
    messageSent: "संदेश भेजा गया!",
    thankYou: "संपर्क करने के लिए धन्यवाद। हम 24-48 घंटों के भीतर जवाब देंगे।",
    subjectOptions: {
      general: "सामान्य पूछताछ",
      support: "तकनीकी सहायता",
      partnership: "साझेदारी का अवसर",
      feedback: "प्रतिक्रिया",
      bug: "बग रिपोर्ट करें"
    }
  },
  zh: {
    title: "联系我们",
    subtitle: "我们很乐意听取您的意见",
    back: "返回",
    generalInquiries: "一般咨询",
    support: "支持",
    partnerships: "合作",
    name: "姓名",
    email: "电子邮件",
    subject: "主题",
    message: "留言",
    yourName: "您的姓名",
    yourEmail: "您的@email.com",
    howCanWeHelp: "我们能帮您什么？",
    sendMessage: "发送消息",
    messageSent: "消息已发送！",
    thankYou: "感谢您的联系。我们将在24-48小时内回复您。",
    subjectOptions: {
      general: "一般咨询",
      support: "技术支持",
      partnership: "合作机会",
      feedback: "反馈",
      bug: "报告错误"
    }
  }
};

const DISCLAIMERS = {
  en: "PlateMind provides nutrition guidance and educational insights only. The information provided does not replace professional medical advice, diagnosis, or treatment. Users should always consult a qualified healthcare professional regarding medical conditions or dietary changes. PlateMind is not a substitute for medical care.",
  es: "PlateMind ofrece orientación nutricional e información educativa únicamente. La información proporcionada no reemplaza el consejo médico profesional, el diagnóstico o el tratamiento. Los usuarios siempre deben consultar a un profesional de la salud calificado sobre condiciones médicas o cambios dietéticos. PlateMind no sustituye la atención médica.",
  fr: "PlateMind fournit des conseils nutritionnels et des informations éducatives uniquement. Les informations fournies ne remplacent pas les conseils médicaux professionnels, le diagnostic ou le traitement. Les utilisateurs doivent toujours consulter un professionnel de santé qualifié concernant les conditions médicales ou les changements alimentaires. PlateMind ne remplace pas les soins médicaux.",
  pt: "A PlateMind fornece orientação nutricional e insights educacionais apenas. As informações fornecidas não substituem aconselhamento médico profissional, diagnóstico ou tratamento. Os usuários devem sempre consultar um profissional de saúde qualificado sobre condições médicas ou mudanças na dieta. A PlateMind não substitui os cuidados médicos.",
  ar: "تقدم PlateMind إرشادات غذائية ومعلومات تعليمية فقط. المعلومات المقدمة لا تحل محل المشورة الطبية المهنية أو التشخيص أو العلاج. يجب على المستخدمين دائماً استشارة أخصائي رعاية صحية مؤهل بشأن الحالات الطبية أو التغييرات الغذائية. PlateMind ليست بديلاً عن الرعاية الطبية.",
  hi: "PlateMind केवल पोषण मार्गदर्शन और शैक्षिक जानकारी प्रदान करता है। प्रदान की गई जानकारी पेशेवर चिकित्सा सलाह, निदान या उपचार का विकल्प नहीं है। उपयोगकर्ताओं को चिकित्सा स्थितियों या आहार परिवर्तनों के बारे में हमेशा योग्य स्वास्थ्य पेशेवर से परामर्श करना चाहिए। PlateMind चिकित्सा देखभाल का विकल्प नहीं है।",
  zh: "PlateMind 仅提供营养指导和教育见解。提供的信息不能替代专业医疗建议、诊断或治疗。用户应始终就医疗状况或饮食变化咨询合格的医疗专业人员。PlateMind 不能替代医疗护理。"
};

// Privacy Policy Page
export const PrivacyPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const content = LEGAL_CONTENT.privacy.en;
  const disclaimer = DISCLAIMERS.en;
  const backText = CONTACT_TRANSLATIONS[language]?.back || CONTACT_TRANSLATIONS.en.back;

  return (
    <div className="min-h-screen bg-[#FFF7F0]">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            {backText}
          </button>
          <LanguageSwitcher variant="compact" />
        </div>
        
        <div className="bg-white rounded-2xl p-8 shadow-sm">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{content.title}</h1>
          <p className="text-sm text-gray-500 mb-8">{content.lastUpdated}</p>
          
          <div className="prose prose-gray max-w-none">
            {content.content.split('\n').map((line, i) => {
              if (line.startsWith('## ')) {
                return <h2 key={i} className="text-xl font-semibold mt-6 mb-3">{line.replace('## ', '')}</h2>;
              }
              if (line.startsWith('- ')) {
                return <li key={i} className="ml-4">{line.replace('- ', '')}</li>;
              }
              if (line.trim()) {
                return <p key={i} className="text-gray-700 mb-2">{line}</p>;
              }
              return null;
            })}
          </div>
          
          <div className="mt-10 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500 italic">{disclaimer}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Terms of Service Page
export const TermsPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const content = LEGAL_CONTENT.terms.en;
  const disclaimer = DISCLAIMERS.en;
  const backText = CONTACT_TRANSLATIONS[language]?.back || CONTACT_TRANSLATIONS.en.back;

  return (
    <div className="min-h-screen bg-[#FFF7F0]">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            {backText}
          </button>
          <LanguageSwitcher variant="compact" />
        </div>
        
        <div className="bg-white rounded-2xl p-8 shadow-sm">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{content.title}</h1>
          <p className="text-sm text-gray-500 mb-8">{content.lastUpdated}</p>
          
          <div className="prose prose-gray max-w-none">
            {content.content.split('\n').map((line, i) => {
              if (line.startsWith('## ')) {
                return <h2 key={i} className="text-xl font-semibold mt-6 mb-3">{line.replace('## ', '')}</h2>;
              }
              if (line.startsWith('- ')) {
                return <li key={i} className="ml-4">{line.replace('- ', '')}</li>;
              }
              if (line.startsWith('**') && line.endsWith('**')) {
                return <p key={i} className="text-gray-900 font-semibold mb-2">{line.replace(/\*\*/g, '')}</p>;
              }
              if (line.trim()) {
                return <p key={i} className="text-gray-700 mb-2">{line}</p>;
              }
              return null;
            })}
          </div>
          
          <div className="mt-10 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500 italic">{disclaimer}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Contact Page
export const ContactPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = CONTACT_TRANSLATIONS[language] || CONTACT_TRANSLATIONS.en;
  const disclaimer = DISCLAIMERS[language] || DISCLAIMERS.en;
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'general',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Contact form submitted:', formData);
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-[#FFF7F0]">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            {t.back}
          </button>
          <LanguageSwitcher variant="compact" />
        </div>
        
        <div className="bg-white rounded-2xl p-8 shadow-sm">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t.title}</h1>
          <p className="text-gray-600 mb-8">{t.subtitle}</p>
          
          {/* Contact Options */}
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <div className="bg-orange-50 rounded-xl p-4 text-center">
              <Mail className="w-8 h-8 text-orange-500 mx-auto mb-2" />
              <h3 className="font-medium text-gray-900">{t.generalInquiries}</h3>
              <a href="mailto:hello@platemind.app" className="text-orange-500 text-sm">hello@platemind.app</a>
            </div>
            <div className="bg-blue-50 rounded-xl p-4 text-center">
              <MessageSquare className="w-8 h-8 text-blue-500 mx-auto mb-2" />
              <h3 className="font-medium text-gray-900">{t.support}</h3>
              <a href="mailto:support@platemind.app" className="text-blue-500 text-sm">support@platemind.app</a>
            </div>
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <Phone className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <h3 className="font-medium text-gray-900">{t.partnerships}</h3>
              <a href="mailto:partnership@platemind.app" className="text-green-500 text-sm">partnership@platemind.app</a>
            </div>
          </div>
          
          {/* Contact Form */}
          {submitted ? (
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
              <h3 className="text-lg font-semibold text-green-800 mb-2">{t.messageSent}</h3>
              <p className="text-green-700">{t.thankYou}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.name}</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder={t.yourName}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.email}</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder={t.yourEmail}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.subject}</label>
                <select
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="general">{t.subjectOptions.general}</option>
                  <option value="support">{t.subjectOptions.support}</option>
                  <option value="partnership">{t.subjectOptions.partnership}</option>
                  <option value="feedback">{t.subjectOptions.feedback}</option>
                  <option value="bug">{t.subjectOptions.bug}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.message}</label>
                <textarea
                  required
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                  placeholder={t.howCanWeHelp}
                />
              </div>
              
              <button
                type="submit"
                className="w-full bg-orange-500 text-white py-3 rounded-xl font-semibold hover:bg-orange-600 transition-colors"
              >
                {t.sendMessage}
              </button>
            </form>
          )}
          
          <div className="mt-10 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500 italic">{disclaimer}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default { PrivacyPage, TermsPage, ContactPage };
